//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import { FanreelsApiModerationStatuses, IApprovedReelsResponse, IItemInReelResult, IMinimalFanreel } from '@fanreels/fanreelsApiModel';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import ReelId from '@models/ReelId';
import { gatewayFetch, sessionFetch } from '@utils/sessionFetch';
import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

const FANREELS_BASE_URL = '/api/v2/reels';

/**
 * Rejects a single item from the given Fanreel/Gallery
 * /api/v2/reels/:reelId/items/:cuid/status
 *
 * @param reelId string - The reelId for the fanreel to reject from
 * @param cuid PartialCuid - The cuid of the item to reject
 *
 * @returns Promise<Void>
 */
export const rejectItemFromFanreel = async (
  cuid: PartialCuid,
  reelId: ReelId,
): Promise<void> => {
  const url = `${FANREELS_BASE_URL}/${reelId}/items/${cuid}/status`;
  const res = await sessionFetch(url, {
    method: 'PUT',
    body: JSON.stringify({
      moderationStatus: FanreelsApiModerationStatuses.REJECTED,
      cuid,
    }),
  });

  return await res.json();
};

/**
 * Gets fanreels
 * /api/v2/reels
 *
 * @returns Promise<IMinimalFanreel[]>
 */
export const getMinimalFanreels = async (): Promise<IMinimalFanreel[]> => {
  const res = await sessionFetch(`${FANREELS_BASE_URL}?noStats=true`);

  const json = await res.json();

  return json.map((fanreel) => {
    return {
      name: fanreel.name,
      id: fanreel.id,
    };
  });
};

/**
 * Determines if a single item exists in a reel
 * /api/v2/reels/:reelId/is-item-in-reel/:cuid
 *
 * @param reelId string - The reelId for the fanreel to check
 * @param cuid PartialCuid - The cuid of the item to check
 *
 * @returns Promise<IItemInReelResult>
 */
export const isItemInReel = async (
    cuid: Cuid,
    reelId: ReelId,
): Promise<IItemInReelResult> => {
  // TODO: Obviate the use cases for this call through MLS
  const url = `${FANREELS_BASE_URL}/${reelId}/is-item-in-reel/${cuid}`;
  const res = await sessionFetch(url, {
    method: 'GET',
  });

  return await res.json();
};

/**
 * Bypasses approvals on a single cuid
 * /api/v2/reels/:reelId/items/:cuid/status
 *
 * @param cuid Cuid - the cuid to bypass approvals on
 * @param reelId ReelId - the reelid of the gallery to bypass approvals for
 *
 * @returns Promise<ReelItemStatusRequest>
 */
export const addItemWithoutApproval = async (
  cuid: Cuid,
  reelId: ReelId,
) => {
  const url = `${FANREELS_BASE_URL}/${reelId}/items/${cuid}/status`;
  const res = await sessionFetch(url, {
    method: 'PUT',
    body: JSON.stringify({
      // TODO: Make moderation status constants to match ModerationStatus enum
      moderationStatus: RIGHTS_STATUS.APPROVED,
      bypassRightsRequest: true,
      cuid,
    }),
  });

  return await res.json();
};

/**
 * Determine which reels a cuid has been approved to
 *
 * @param cuids The cuid that may be approved to reels
 */

export const getApprovedReels = async (cuids: Cuid[]) => {
  const res = await gatewayFetch('web_gateway', '/api/v1/media/get-approved-reels-for-cuids', {
    method: 'POST',
    body: JSON.stringify({
      cuids,
    }),
  });

  const jsonResp: IApprovedReelsResponse = await res.json();

  return jsonResp.data;
};
